import React, { useMemo } from 'react';
import { categorizeEmployee, calculateTrainingHours, workforceHeadcountFromCoverage, getCurrentFinancialYearLabel } from '../../utils/trainingUtils';
import { TrainingIcon } from '../TrainingIcon';

/**
 * Computes a coverage percentage (numerator / denominator), guarding divide-by-zero.
 */
const coveragePercent = (covered, total) =>
  total > 0 ? ((covered / total) * 100).toFixed(2) : "0.00";

/**
 * Renders the line under a card's headline count: the "% covered" against the
 * company-wide total when it's available, otherwise an inline warning telling the
 * user the denominator isn't reported in the quantitative data for the selected year.
 */
const CoverageDenominator = ({ covered, total }) => {
  if (total > 0) {
    const percent = coveragePercent(covered, total);
    const exceeds = Number(percent) > 100;
    return (
      <>
        <div className="trd-stat-cover">{percent}% covered</div>
        {exceeds && (
          <div
            className="trd-stat-warn trd-stat-warn--error"
            title="The trained/registered count is higher than the reported headcount for this category, so the underlying training data is likely inaccurate (e.g. duplicate or cross-entity attendance, or an under-reported headcount)."
          >
            <TrainingIcon name="alert" size={12} />
            <span>Over 100% — training data may be inaccurate</span>
          </div>
        )}
      </>
    );
  }
  return (
    <div
      className="trd-stat-warn"
      title="Company total for this category isn't reported in the quantitative data for the selected year, so coverage % can't be calculated."
    >
      <TrainingIcon name="alert" size={12} />
      <span>Company total unavailable</span>
    </div>
  );
};

/**
 * Shared frame for every summary/coverage stat card: accent bar + icon badge,
 * uppercase title, headline value with an optional "/ denominator", the coverage
 * line, and an optional list of metric rows passed as children.
 */
const StatCardShell = ({ accent, accentSoft, icon, title, subtitle, value, denominator, children }) => {
  const hasDenominator = denominator !== undefined && denominator !== null;
  return (
    <div className="trd-stat-card" style={{ "--trd-accent": accent, "--trd-accent-soft": accentSoft }}>
      <div className="trd-stat-head">
        <div>
          <h3 className="trd-stat-title">{title}</h3>
          {subtitle && <span className="trd-stat-subtitle">{subtitle}</span>}
        </div>
        <span className="trd-stat-icon"><TrainingIcon name={icon} size={17} /></span>
      </div>
      <div className="trd-stat-value">
        {value ?? "—"}
        {hasDenominator && denominator > 0 && (
          <span className="trd-stat-value-den"> / {denominator}</span>
        )}
      </div>
      {hasDenominator && <CoverageDenominator covered={value} total={denominator} />}
      {children && <div className="trd-stat-meta">{children}</div>}
    </div>
  );
};

/**
 * A single label/value metric row inside a stat card's meta section.
 */
const MetricRow = ({ label, value, tone }) => (
  <div className="trd-metric">
    <span className="trd-metric-label">{label}</span>
    <span className={`trd-metric-value${tone ? ` trd-metric-value--${tone}` : ""}`}>{value}</span>
  </div>
);

/**
 * Top summary row: total programs, employee/worker coverage (each compared against
 * its company-wide headcount) and monthly participants (compared against the full
 * workforce). Values, denominators and percentages come fully computed from the
 * training-dashboard analytics endpoint (`analytics`).
 */
export const StatsCards = ({ analytics, loading }) => {
  const {
    totalTrainingPrograms,
    employeeCoverage = {},
    workerCoverage = {},
    monthlyParticipants = {}
  } = analytics || {};

  const metric = (count, percent) => `${count ?? "—"} (${percent ?? "—"}%)`;

  return (
    <div className={`trd-stat-grid${loading ? " trd-loading" : ""}`}>
      <StatCardShell
        accent="#3B82F6"
        accentSoft="#eff6ff"
        icon="layers"
        title="Total Training Programs"
        value={totalTrainingPrograms}
      />

      <StatCardShell
        accent="#8B5CF6"
        accentSoft="#f5f3ff"
        icon="users"
        title="Employee Coverage"
        subtitle="Including KMPs"
        value={employeeCoverage.total}
        denominator={employeeCoverage.headcount}
      >
        <MetricRow label="Health & Safety" tone="blue" value={metric(employeeCoverage.healthSafety, employeeCoverage.healthSafetyPercent)} />
        <MetricRow label="Skill Development" tone="green" value={metric(employeeCoverage.skillDevelopment, employeeCoverage.skillDevelopmentPercent)} />
      </StatCardShell>

      <StatCardShell
        accent="#F97316"
        accentSoft="#fff5ed"
        icon="briefcase"
        title="Worker Coverage"
        value={workerCoverage.total}
        denominator={workerCoverage.headcount}
      >
        <MetricRow label="Health & Safety" tone="blue" value={metric(workerCoverage.healthSafety, workerCoverage.healthSafetyPercent)} />
        <MetricRow label="Skill Development" tone="green" value={metric(workerCoverage.skillDevelopment, workerCoverage.skillDevelopmentPercent)} />
      </StatCardShell>

      <StatCardShell
        accent="#10B981"
        accentSoft="#ecfdf5"
        icon="activity"
        title="Yearly Participants"
        value={monthlyParticipants.count}
        denominator={monthlyParticipants.headcount}
      >
        <MetricRow label="Training Hours" tone="blue" value={monthlyParticipants.trainingHours ?? "—"} />
      </StatCardShell>
    </div>
  );
};

/**
 * Drives the EmployeeStats cards: each entry maps the trained/registered coverage
 * bucket (from useProcessedEmployeeData) to its company-wide headcount bucket
 * (keyed by HEADCOUNT_TITLE_IDS) plus the card's title, icon and accent tokens.
 */
const EMPLOYEE_COVERAGE_CARDS = [
  { coverageKey: "boardOfDirectors", title: "Board of Directors (BOD)", icon: "shield", accent: "#DC2626", accentSoft: "#fef2f2" },
  { coverageKey: "keyManagerialPersonnel", title: "Key Managerial Personnel (KMP)", icon: "award", accent: "#7C3AED", accentSoft: "#f5f3ff" },
  { coverageKey: "permanentEmployee", title: "Permanent Employees", subtitle: "Including KMPs", icon: "user", accent: "#3B82F6", accentSoft: "#eff6ff" },
  { coverageKey: "otherThanPermanentEmployee", title: "Other than Permanent Employees", icon: "users", accent: "#0891B2", accentSoft: "#ecfeff" },
  { coverageKey: "permanentWorker", title: "Permanent Workers", icon: "briefcase", accent: "#F97316", accentSoft: "#fff5ed" },
  { coverageKey: "otherThanPermanentWorker", title: "Other than Permanent Workers", icon: "clock", accent: "#10B981", accentSoft: "#ecfdf5" }
];

/**
 * Single employee/worker coverage card. Renders the trained/registered count and,
 * when the company-wide headcount is known, the "out of <total>" denominator with a
 * coverage percentage; otherwise it falls back to the gender split of the trained set.
 */
const CoverageStatCard = React.memo(function CoverageStatCard({ title, subtitle, icon, accent, accentSoft, trained, headcount }) {
  const cover = trained || { total: 0, male: 0, female: 0, malePercent: "0.00", femalePercent: "0.00" };
  const head = headcount || { total: 0, male: 0, female: 0 };
  const hasHeadcount = head.total > 0;

  const overGenders = hasHeadcount
    ? [cover.male > head.male && "Male", cover.female > head.female && "Female"].filter(Boolean)
    : [];

  return (
    <StatCardShell accent={accent} accentSoft={accentSoft} icon={icon} title={title} subtitle={subtitle} value={cover.total} denominator={head.total}>
      <MetricRow
        label="Male"
        tone="blue"
        value={hasHeadcount
          ? `${cover.male} / ${head.male} (${coveragePercent(cover.male, head.male)}%)`
          : `${cover.male} (${cover.malePercent}%)`}
      />
      <MetricRow
        label="Female"
        tone="pink"
        value={hasHeadcount
          ? `${cover.female} / ${head.female} (${coveragePercent(cover.female, head.female)}%)`
          : `${cover.female} (${cover.femalePercent}%)`}
      />
      {overGenders.length > 0 && (
        <div
          className="trd-stat-warn trd-stat-warn--error"
          title="A gender's trained/registered count exceeds its reported headcount, so the underlying training data is likely inaccurate."
        >
          <TrainingIcon name="alert" size={12} />
          <span>{overGenders.join(" & ")} over 100% — training data may be inaccurate</span>
        </div>
      )}
    </StatCardShell>
  );
});

/**
 * Grid of employee/worker coverage cards. Each card's registered-trainee coverage
 * bucket (count + gender split) and its company-wide headcount denominator come
 * fully computed from the analytics endpoint (`analytics.categoryCoverage`).
 */
export const EmployeesStatsCards = ({ analytics, loading }) => {
  const categoryCoverage = (analytics && analytics.categoryCoverage) || {};
  return (
    <div className={`trd-emp-grid${loading ? " trd-loading" : ""}`}>
      {EMPLOYEE_COVERAGE_CARDS.map((card) => {
        const block = categoryCoverage[card.coverageKey];
        return (
          <CoverageStatCard
            key={card.coverageKey}
            title={card.title}
            subtitle={card.subtitle}
            icon={card.icon}
            accent={card.accent}
            accentSoft={card.accentSoft}
            trained={block}
            headcount={block && block.headcount}
          />
        );
      })}
    </div>
  );
};

/**
 * Renders one "Employee / Worker Training Numbers" column in the coverage summary.
 */
const SummaryColumn = ({ icon, title, rows }) => (
  <div>
    <h3 className="trd-block-title"><TrainingIcon name={icon} size={15} />{title}</h3>
    <div className="trd-stat-meta" style={{ marginTop: 0, paddingTop: 0, borderTop: "none", gap: 10 }}>
      {rows.map((row) => (
        <MetricRow key={row.label} label={row.label} value={row.value} tone={row.tone} />
      ))}
    </div>
  </div>
);

export const TrainingCoverageSummary = ({ trainingData, traineeList, financialYear, financialYearId, categoryCoverage, categoryIds, locationFocusLabel = 'All' }) => {
  const summaryData = useMemo(() => {
    const summary = {
      employees: { total: 0, healthSafety: new Set(), skillDevelopment: new Set(), humanRights: new Set() },
      workers: { total: 0, healthSafety: new Set(), skillDevelopment: new Set(), humanRights: new Set() },
      performance: {
        totalPrograms: 0,
        participants: new Set(),
        totalParticipants: 0,
        trainingHours: 0
      }
    };

    const now = new Date();
    const selectedFY = Array.isArray(financialYear)
      ? financialYear.find(fy => fy.id === financialYearId)
      : null;
    const isCurrentFY = !!selectedFY &&
      !isNaN(new Date(selectedFY.fromDate).getTime()) &&
      now >= new Date(selectedFY.fromDate) &&
      now <= new Date(selectedFY.toDate);
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    if (categoryCoverage) {
      const workforce = workforceHeadcountFromCoverage(categoryCoverage);
      summary.employees.total = workforce.employees.total;
      summary.workers.total = workforce.workers.total;
    } else if (Array.isArray(traineeList)) {
      traineeList.forEach(trainee => {
        if (!trainee) return;

        const category = categorizeEmployee(trainee);

        if (category === 'Employees other than BoD and KMPs' ||
            category === 'Key Managerial Personnel') {
          summary.employees.total++;
        } else if (category === 'Workers') {
          summary.workers.total++;
        }
      });
    }

    if (trainingData && Array.isArray(trainingData)) {
      trainingData.forEach(training => {
        if (!training) return;

        const attendants = training.attendantUsers || [];
        const trainingCategoryIds = (training.categories || []).map(category => category?.id).filter(Boolean);

        const trainingDate = new Date(training.fromDate);
        const inPerformanceWindow = !isCurrentFY ||
          (!isNaN(trainingDate.getTime()) &&
            trainingDate.getMonth() === currentMonth &&
            trainingDate.getFullYear() === currentYear);

        if (inPerformanceWindow) {
          summary.performance.totalPrograms++;
          summary.performance.trainingHours += calculateTrainingHours(training.fromTime, training.toTime);
        }

        attendants.forEach(attendant => {
          if (!attendant) return;

          const employeeCategory = categorizeEmployee(attendant);
          const employeeId = attendant.id;

          if (!employeeId) return;

          if (inPerformanceWindow) summary.performance.participants.add(employeeId);

          let targetCategory = null;
          if (employeeCategory === 'Employees other than BoD and KMPs' ||
              employeeCategory === 'Key Managerial Personnel') {
            targetCategory = 'employees';
          } else if (employeeCategory === 'Workers') {
            targetCategory = 'workers';
          }

          if (!targetCategory) return;

          if (categoryIds?.healthSafety != null && trainingCategoryIds.includes(categoryIds.healthSafety)) {
            summary[targetCategory].healthSafety.add(employeeId);
          }

          if (categoryIds?.skillDevelopment != null && trainingCategoryIds.includes(categoryIds.skillDevelopment)) {
            summary[targetCategory].skillDevelopment.add(employeeId);
          }

          if (categoryIds?.humanRights != null && trainingCategoryIds.includes(categoryIds.humanRights)) {
            summary[targetCategory].humanRights.add(employeeId);
          }
        });
      });
    }

    ['employees', 'workers'].forEach(category => {
      const total = summary[category].total;
      summary[category].healthSafetyCount = summary[category].healthSafety.size;
      summary[category].healthSafetyPercent = total > 0 ? ((summary[category].healthSafety.size / total) * 100).toFixed(2) : '0.00';

      summary[category].skillDevelopmentCount = summary[category].skillDevelopment.size;
      summary[category].skillDevelopmentPercent = total > 0 ? ((summary[category].skillDevelopment.size / total) * 100).toFixed(2) : '0.00';

      summary[category].humanRightsCount = summary[category].humanRights.size;
      summary[category].humanRightsPercent = total > 0 ? ((summary[category].humanRights.size / total) * 100).toFixed(2) : '0.00';

      delete summary[category].healthSafety;
      delete summary[category].skillDevelopment;
      delete summary[category].humanRights;
    });

    summary.performance.totalParticipants = summary.performance.participants.size;
    summary.performance.trainingHours = Math.round(summary.performance.trainingHours);
    delete summary.performance.participants;

    summary.performance.title = isCurrentFY
      ? `Monthly Performance (${now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })})`
      : `Training Performance (${getCurrentFinancialYearLabel(financialYear, financialYearId)})`;

    return summary;
  }, [trainingData, traineeList, categoryCoverage, categoryIds, financialYear, financialYearId]);

  return (
    <div className="trd-section">
      <div className="trd-section-head">
        <span className="trd-section-icon"><TrainingIcon name="bars" size={18} /></span>
        <div>
          <h2 className="trd-section-title">Training Coverage Summary</h2>
          <p className="trd-section-sub">Unique participants trained across categories</p>
        </div>
      </div>

      <div className="trd-bucket-grid">
        <SummaryColumn
          icon="users"
          title="Employee Training Numbers"
          rows={[
            { label: "Total Employees:", value: summaryData.employees.total },
            { label: "Health & Safety Trained", tone: "blue", value: `${summaryData.employees.healthSafetyCount} (${summaryData.employees.healthSafetyPercent}%)` },
            { label: "Skill Development Trained", tone: "green", value: `${summaryData.employees.skillDevelopmentCount} (${summaryData.employees.skillDevelopmentPercent}%)` },
            { label: "Human Rights Trained", tone: summaryData.employees.humanRightsCount > 0 ? "amber" : "red", value: `${summaryData.employees.humanRightsCount} (${summaryData.employees.humanRightsPercent}%)` }
          ]}
        />
        <SummaryColumn
          icon="briefcase"
          title="Worker Training Numbers"
          rows={[
            { label: "Total Workers", value: summaryData.workers.total },
            { label: "Health & Safety Trained", tone: "blue", value: `${summaryData.workers.healthSafetyCount} (${summaryData.workers.healthSafetyPercent}%)` },
            { label: "Skill Development Trained", tone: "green", value: `${summaryData.workers.skillDevelopmentCount} (${summaryData.workers.skillDevelopmentPercent}%)` },
            { label: "Human Rights Trained", tone: summaryData.workers.humanRightsCount > 0 ? "amber" : "red", value: `${summaryData.workers.humanRightsCount} (${summaryData.workers.humanRightsPercent}%)` }
          ]}
        />
        <SummaryColumn
          icon="calendar"
          title={summaryData.performance.title}
          rows={[
            { label: "Total Programs", tone: "amber", value: summaryData.performance.totalPrograms },
            { label: "Total Participants", tone: "green", value: summaryData.performance.totalParticipants },
            { label: "Training Hours", tone: "blue", value: summaryData.performance.trainingHours },
            { label: "Location Focus", tone: "amber", value: locationFocusLabel }
          ]}
        />
      </div>
    </div>
  );
};
